import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../utils/FirebaseConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/ImageProfail.css";
import { saveProfile } from "../services/mosqueContentRepository";

const DEFAULT_PLACEHOLDER = "/images/150.png";

/* =========================
   Helpers
========================= */
const extractStoragePathFromUrl = (url) => {
    try {
        const decoded = decodeURIComponent(url);
        const m = decoded.match(/\/o\/(.*?)\?/);
        return m ? m[1] : null;
    } catch {
        return null;
    }
};

const deleteByUrl = async (url) => {
    if (!url) return;
    if (!String(url).includes("/o/")) return;

    const path = extractStoragePathFromUrl(url);
    if (!path) return;

    await deleteObject(ref(storage, path));
};

const fieldFromId = (id) => (id === 1 ? "logo" : id === 2 ? "gambar" : "qr");

const maxSizeByField = (field) => {
    if (field === "logo") return 600;
    if (field === "qr") return 900;
    return 1200; // gambar masjid
};

// Decide output type based on field + input file
const getOutputTypeForField = (field, file) => {
    const inType = (file?.type || "").toLowerCase();

    // Logo: preserve png if png, else jpeg
    if (field === "logo") {
        return inType === "image/png" ? "image/png" : "image/jpeg";
    }

    // Gambar masjid: always jpeg
    if (field === "gambar") {
        return "image/jpeg";
    }

    // QR: always png (crisper)
    if (field === "qr") {
        return "image/png";
    }

    // fallback
    return inType === "image/png" ? "image/png" : "image/jpeg";
};

// Resize + compress (jpeg quality used only for jpeg)
const resizeImage = (file, { maxSize = 900, outType = "image/jpeg", quality = 0.82 }) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        const canvas = document.createElement("canvas");

        reader.onload = (e) => {
            img.onload = () => {
                const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);

                canvas.width = w;
                canvas.height = h;

                const ctx = canvas.getContext("2d");

                // IMPORTANT: if output is JPEG, fill background white to avoid black bg
                // if output is PNG, keep transparency
                if (outType === "image/jpeg") {
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, w, h);
                }

                ctx.drawImage(img, 0, 0, w, h);

                canvas.toBlob(
                    (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
                    outType,
                    outType === "image/jpeg" ? quality : undefined
                );
            };

            img.onerror = () => reject(new Error("Invalid image"));
            img.src = e.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

// ✅ NEW: Build OG 1200x630 (cover crop)
const buildOgImage = (file, { width = 1200, height = 630, outType = "image/jpeg", quality = 0.86 } = {}) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        const canvas = document.createElement("canvas");

        reader.onload = (e) => {
            img.onload = () => {
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");

                // jpeg background
                if (outType === "image/jpeg") {
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, width, height);
                }

                // cover scale
                const scale = Math.max(width / img.width, height / img.height);
                const sw = img.width * scale;
                const sh = img.height * scale;
                const dx = (width - sw) / 2;
                const dy = (height - sh) / 2;

                ctx.drawImage(img, dx, dy, sw, sh);

                canvas.toBlob(
                    (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed (og)"))),
                    outType,
                    outType === "image/jpeg" ? quality : undefined
                );
            };

            img.onerror = () => reject(new Error("Invalid image (og)"));
            img.src = e.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

// Upload new + delete old (replace)
const uploadReplace = async ({ newPath, fileOrBlob, oldUrl, contentType }) => {
    try {
        await deleteByUrl(oldUrl);
    } catch {
        // silent
    }

    const r = ref(storage, newPath);
    await uploadBytes(r, fileOrBlob, contentType ? { contentType } : undefined);
    return await getDownloadURL(r);
};

const extFromType = (mime) => {
    if (mime === "image/png") return "png";
    return "jpg";
};

const ImageProfail = ({ id, label, mosqueId, imagePath, autoOg = false }) => {
    const [image, setImage] = useState(imagePath || DEFAULT_PLACEHOLDER);

    useEffect(() => {
        setImage(imagePath || DEFAULT_PLACEHOLDER);
    }, [imagePath]);

    const handleImageChange = async () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";

        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            if (!mosqueId) {
                toast.error("Mosque ID tidak ada. Sila login semula.");
                return;
            }

            try {
                const docRef = doc(db, "masajid", mosqueId);
                const docSnap = await getDoc(docRef);

                const field = fieldFromId(id);

                let oldUrl = "";
                let oldOgUrl = "";
                if (docSnap.exists()) {
                    oldUrl = docSnap.data()?.profail?.[field] || "";
                    oldOgUrl = docSnap.data()?.profail?.ogImage || "";
                }

                const maxSize = maxSizeByField(field);
                const outType = getOutputTypeForField(field, file);

                // resize + encode (main image)
                const blob = await resizeImage(file, {
                    maxSize,
                    outType,
                    quality: 0.82,
                });

                const ext = extFromType(outType);
                const newPath = `${mosqueId}/profailImages/${field}_${Date.now()}.${ext}`;

                const newUrl = await uploadReplace({
                    newPath,
                    fileOrBlob: blob,
                    oldUrl,
                    contentType: outType,
                });

                // update state + firestore (main image)
                setImage(newUrl);
                await saveProfile(mosqueId, { [field]: newUrl });

                // ✅ AUTO OG (hanya bila upload gambar masjid)
                if (autoOg && field === "gambar") {
                    try {
                        const ogType = "image/jpeg";
                        const ogBlob = await buildOgImage(file, { width: 1200, height: 630, outType: ogType, quality: 0.86 });

                        const ogPath = `${mosqueId}/profailImages/og_${Date.now()}.jpg`;
                        const ogUrl = await uploadReplace({
                            newPath: ogPath,
                            fileOrBlob: ogBlob,
                            oldUrl: oldOgUrl,
                            contentType: ogType,
                        });

                        await saveProfile(mosqueId, { ogImage: ogUrl });
                    } catch (e) {
                        console.warn("⚠️ OG generate/upload gagal:", e);
                        // tak gagalkan upload asal
                    }
                }

                toast.success("Image uploaded successfully!");
            } catch (error) {
                console.error("Error uploading image:", error);
                toast.error("Failed to upload image. Please try again.");
            }
        };

        input.click();
    };

    return (
        <div className="image-profail-container">
            <button className="change-button" onClick={handleImageChange}>
                Change
            </button>

            <img
                src={image}
                alt={label}
                className="profail-image"
                onError={(e) => {
                    e.currentTarget.src = DEFAULT_PLACEHOLDER;
                }}
            />

            <p className="image-label">{label}</p>
        </div>
    );
};

export default ImageProfail;
